import {
  dateKeyToLocalDate,
  formatDate,
  isDateKeyShaped,
} from "@ergon-ai/utils";

/**
 * A date-only string ("2026-09-15") parses as UTC MIDNIGHT (ES spec), so
 * rendering it through Intl in a negative-offset timezone shows the
 * PREVIOUS day (repro: TZ=America/New_York → "14/09/2026"). Resolve it to
 * LOCAL midnight instead — the calendar day the user booked is stable in
 * every timezone. Anything else passes through unchanged, including keys
 * that only LOOK like a date ("2026-13-45" must never roll over to a real
 * February 2027 day).
 *
 * Not part of the package's public surface (it is off both index barrels):
 * the parse has ONE implementation repo-wide (`dateKeyToLocalDate` in
 * @ergon-ai/utils, EAH-1969) and app call sites that need the `null` import
 * that one. This is only the string-tolerant shim `getFormattedDate` needs.
 */
export const parseDateOnlyLocal = (value: string): Date | string =>
  dateKeyToLocalDate(value) ?? value;

/**
 * Formats a date into the locale's absolute date format (it-IT DD/MM/YYYY,
 * en-US MM/DD/YYYY). Date-only strings resolve at LOCAL midnight (see
 * `parseDateOnlyLocal`) so the day never shifts with the device timezone.
 *
 * A day-shaped key the strict parse refuses comes back UNCHANGED, never a
 * rolled-over day (EAH-2065); so does anything `new Date()` cannot read.
 * It does NOT log: this package ships no logger and this runs on every
 * render. A caller that wants its own fallback, or a signal, tests the key
 * with `dateKeyToLocalDate` first — which is what both chat sites do.
 *
 * @param date - The date to format (Date object or ISO string).
 * @param locale - Locale string (`"en-GB"`, `"it-IT"`…). Defaults to `"en-GB"`.
 * @returns The formatted date, or the input string when it is not renderable.
 *
 * @example
 * ```ts
 * getFormattedDate("2024-01-15", "en-GB"); // "15/01/2024" in every timezone
 * getFormattedDate("2026-02-30", "en-GB"); // "2026-02-30" — never 02/03/2026
 * ```
 */
export const getFormattedDate = (
  date: Date | string,
  locale = "en-GB",
): string => {
  const parsed = typeof date === "string" ? parseDateOnlyLocal(date) : date;
  if (typeof parsed !== "string") return formatDate(parsed, locale);

  // Refused by the strict parse and day-shaped, so it is an impossible day.
  // Handing it to `new Date()` printed 02/03/2026 for "2026-02-30" and made
  // Intl throw `RangeError: Invalid time value` mid-render for "2026-13-45".
  // The shape lives beside the parse in @ergon-ai/utils so the two regexes
  // cannot drift apart in separate packages.
  if (isDateKeyShaped(parsed)) return parsed;

  // A datetime, or junk. Format only what `new Date()` actually understood.
  const dateObj = new Date(parsed);
  return Number.isNaN(dateObj.getTime()) ? parsed : formatDate(dateObj, locale);
};
